/**
 * fc-encryption/crypto/salts.ts
 * 
 * Salt and IV generation using Web Crypto API.
 * Per-block: 16-byte salt, 12-byte IV.
 * Per-key (KDF): 16-byte salt.
 */

const SALT_BYTES = 16;
const IV_BYTES = 12;

/**
 * Generate a 16-byte random salt.
 * Used both per-block (for the EncryptedBlock record) and per-key (for the KDF).
 * 
 * @returns base64 string of 16 random bytes
 */
export function generateSalt(): string {
  const arr = new Uint8Array(SALT_BYTES);
  crypto.getRandomValues(arr);
  return arrayBufferToBase64(arr.buffer);
}

/**
 * Generate a 12-byte random IV for AES-GCM.
 * 
 * AES-GCM is catastrophically broken if the same IV is reused with the same key.
 * This function MUST return a fresh IV on every call. There is no exception.
 * 
 * @returns base64 string of 12 random bytes
 */
export function generateIV(): string {
  const arr = new Uint8Array(IV_BYTES);
  crypto.getRandomValues(arr);
  return arrayBufferToBase64(arr.buffer);
}

/**
 * Convert an ArrayBuffer to a base64 string.
 */
export function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert a base64 string to an ArrayBuffer.
 */
export function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}
